import React from 'react';
import { ChevronRight, Info } from 'react-feather';
import { Variable, CorrelationResult } from '../api/client';

interface Props {
  dependentVariables: Variable[];
  independentVariables: Variable[];
  correlations?: Record<string, Record<string, CorrelationResult>>;
}

interface CorrelationRow {
  dependent: Variable;
  independent: Variable;
  result?: CorrelationResult;
  strength: number;
  confidence: number;
  score: number;
}

const CorrelationView: React.FC<Props> = ({ dependentVariables, independentVariables, correlations }) => {
  const rows = buildRows(dependentVariables, independentVariables, correlations);

  if (dependentVariables.length === 0 || independentVariables.length === 0) {
    return (
      <div className="correlation-view">
        <h3>Correlation</h3>
        <p className="empty-state">
          Add at least one dependent variable and one independent variable to see associations.
        </p>
      </div>
    );
  }

  return (
    <div className="correlation-view">
      <div className="correlation-heading">
        <h3>What seems to matter?</h3>
        <p>Ranked by strength of association and confidence in each result.</p>
      </div>

      <div className="correlation-list">
        {rows.map((row, index) => (
          <details key={`${row.dependent.id}-${row.independent.id}`} className="correlation-row">
            <summary className="correlation-row-summary">
              <span className="correlation-rank">#{index + 1}</span>
              <span className="correlation-main">
                <span className="correlation-title">
                  <span>{row.independent.name}</span>
                  <ChevronRight size={16} aria-hidden="true" />
                  <span>{row.dependent.name}</span>
                </span>
              </span>
              <span className="correlation-strength">
                <span className="correlation-metric-label">Strength</span>
                <span className="correlation-bar" aria-hidden="true">
                  <span
                    className="correlation-bar-fill"
                    style={{ width: `${Math.max(4, row.strength * 100)}%` }}
                  />
                </span>
              </span>
              <span className={`correlation-confidence ${getConfidenceClass(row)}`}>
                {getConfidenceLabel(row)}
              </span>
              <span className="correlation-meta">
                {formatSampleSize(row.result)}
              </span>
            </summary>

            <div className="correlation-details">
              {row.result?.error ? (
                <p className="error-text">{row.result.error}</p>
              ) : row.result ? (
                <>
                  <div>
                    <span className="correlation-detail-label">
                      Effect size
                      <InfoTooltip text="How strong the link looks. Bigger numbers mean the two variables move together more clearly." />
                    </span>
                    <strong>{formatEffect(row.result)}</strong>
                  </div>
                  <div>
                    <span className="correlation-detail-label">
                      p-value
                      <InfoTooltip text="How surprising this pattern would be if there were no real link. Smaller values make the result more believable." />
                    </span>
                    <strong>{formatPValue(row.result)}</strong>
                  </div>
                  <div>
                    <span className="correlation-detail-label">
                      Confidence score
                      <InfoTooltip text="How much to trust this result. It goes up when there are more entries and the p-value makes the pattern look less likely to be random." />
                    </span>
                    <strong>{Math.round(row.confidence * 100)}%</strong>
                  </div>
                  <div>
                    <span className="correlation-detail-label">
                      Method
                      <InfoTooltip text="The kind of math used for this pair. The app picks one based on the variable types." />
                    </span>
                    <strong>{row.result.method}</strong>
                  </div>
                  <div>
                    <span className="correlation-detail-label">
                      Direction
                      <InfoTooltip text="Whether the variables tend to rise together, move opposite ways, or barely move together." />
                    </span>
                    <strong>{getDirection(row.result)}</strong>
                  </div>
                </>
              ) : (
                <p className="correlation-empty-detail">No correlation result has been computed for this pair yet.</p>
              )}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
};

const InfoTooltip: React.FC<{ text: string }> = ({ text }) => (
  <span
    className="value-description-tooltip correlation-info-tooltip"
    data-tooltip={text}
    tabIndex={0}
  >
    <Info size={11} className="value-info-icon" />
  </span>
);

function buildRows(
  dependentVariables: Variable[],
  independentVariables: Variable[],
  correlations?: Record<string, Record<string, CorrelationResult>>
): CorrelationRow[] {
  return dependentVariables
    .flatMap((dependent) =>
      independentVariables.map((independent) => {
        const result = correlations?.[dependent.id]?.[independent.id];
        const strength = result && !result.error ? Math.abs(result.effect_size) : 0;
        const confidence = getConfidenceScore(result);

        return {
          dependent,
          independent,
          result,
          strength,
          confidence,
          score: strength * confidence,
        };
      })
    )
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      if (b.strength !== a.strength) return b.strength - a.strength;
      if (b.confidence !== a.confidence) return b.confidence - a.confidence;
      return a.independent.name.localeCompare(b.independent.name);
    });
}

function getConfidenceScore(result?: CorrelationResult): number {
  if (!result || result.error || result.sample_size <= 0) return 0;

  const p = Math.max(0, Math.min(1, result.p_value));
  const pScore =
    p < 0.001 ? 1 :
    p < 0.01 ? 0.9 :
    p < 0.05 ? 0.75 :
    p < 0.1 ? 0.45 :
    p < 0.2 ? 0.25 :
    0.1;
  const sampleScore = Math.min(1, result.sample_size / 30);

  return pScore * sampleScore;
}

function getConfidenceClass(row: CorrelationRow): string {
  if (!row.result || row.result.error) return 'need-data';
  if (row.confidence >= 0.7 && row.strength >= 0.25) return 'high';
  if (row.confidence >= 0.35 && row.strength >= 0.15) return 'moderate';
  return 'low';
}

function getConfidenceLabel(row: CorrelationRow): string {
  if (!row.result || row.result.error) return 'Need more data';
  if (row.strength < 0.1) return 'Low effect';
  if (row.confidence >= 0.7) return 'High confidence';
  if (row.confidence >= 0.35) return 'Moderate confidence';
  return 'Low confidence';
}

function formatEffect(result?: CorrelationResult): string {
  if (!result || result.error) return '-';
  return `${getEffectSymbol(result.method)} = ${result.effect_size.toFixed(2)}`;
}

function getEffectSymbol(method?: string): string {
  switch (method) {
    case 'spearman':
    case 'mann-whitney': // rank-biserial correlation
      return 'r';
    case 'kruskal-wallis': // epsilon-squared
      return 'ε²';
    default:
      return 'φ';
  }
}

function formatPValue(result?: CorrelationResult): string {
  if (!result || result.error) return 'p = -';
  return result.p_value < 0.001 ? 'p < 0.001' : `p = ${result.p_value.toFixed(3)}`;
}

function formatSampleSize(result?: CorrelationResult): string {
  if (!result) return '- entries';
  return `${result.sample_size} ${result.sample_size === 1 ? 'entry' : 'entries'}`;
}

function getDirection(result: CorrelationResult): string {
  if (Math.abs(result.effect_size) < 0.1) return 'little movement';
  return result.effect_size < 0 ? 'opposite directions' : 'together';
}

export default CorrelationView;
